import { db } from '../../../integrations/firebase';
import { ref, push, set, update, serverTimestamp, query, orderByChild, equalTo, get, onChildAdded, remove } from 'firebase/database';
import { messaging, getToken, onMessage } from '../../../integrations/firebase';

export const NotificationService = {
    // Send a notification to a user
    send: async (userId, notificationData) => {
        try {
            const notificationsRef = ref(db, `notifications/${userId}`);
            const newNotificationRef = push(notificationsRef);

            await set(newNotificationRef, {
                ...notificationData,
                timestamp: serverTimestamp(),
                read: false,
                seen: false
            });

            return { success: true, id: newNotificationRef.key };
        } catch (error) {
            console.error("Error sending notification:", error);
            return { success: false, error: error.message };
        }
    },

    // Mark a notification as read
    markAsRead: async (userId, notificationId) => {
        try {
            const notificationRef = ref(db, `notifications/${userId}/${notificationId}`);
            await update(notificationRef, { read: true, seen: true });
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    },

    // Mark all notifications as seen (when opening dropdown)
    markAllAsSeen: async (userId) => {
        try {
            const notificationsRef = ref(db, `notifications/${userId}`);
            const snapshot = await get(notificationsRef);

            if (snapshot.exists()) {
                const updates = {};
                snapshot.forEach((childSnapshot) => {
                    if (!childSnapshot.val().seen) {
                        updates[`${childSnapshot.key}/seen`] = true;
                    }
                });

                if (Object.keys(updates).length > 0) {
                    await update(notificationsRef, updates);
                }
            }
        } catch (error) {
            console.error("Error marking all as seen:", error);
        }
    },

    // Clear all notifications
    clearAll: async (userId) => {
        try {
            const notificationsRef = ref(db, `notifications/${userId}`);
            await remove(notificationsRef);
        } catch (error) {
            console.error("Error clearing notifications:", error);
        }
    },

    // Listen for new notifications (Real-time Toast Trigger)
    listenForNewNotifications: (userId, onNewNotification) => {
        const notificationsRef = ref(db, `notifications/${userId}`);
        // Listen for added children that are NOT seen yet
        // Since we can't easily query "added after now" without a timestamp reference, 
        // we'll rely on the client to filter or just show all "new" ones that arrive while connected.
        // A better approach for toasts is `onChildAdded` with `limitToLast(1)` but that might miss burst.
        // We'll use `onChildAdded` and check timestamp client-side if needed, or just show all incoming.

        const unsubscribe = onChildAdded(notificationsRef, (snapshot) => {
            const notification = snapshot.val();
            // Only trigger for notifications created roughly now (to avoid flooding on reload)
            // 10 seconds buffer
            if (notification.timestamp && Date.now() - notification.timestamp < 10000) {
                onNewNotification({ ...notification, id: snapshot.key });
            }
        });

        return unsubscribe;
    },

    // Request FCM Permission & Get Token
    requestPermission: async (userId) => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                // Get Token
                // IMPORTANT: Replace with your VAPID Key from Firebase Console -> Project Settings -> Cloud Messaging
                const vapidKey = "YOUR_PUBLIC_VAPID_KEY";

                if (vapidKey === "YOUR_PUBLIC_VAPID_KEY") {
                    console.warn("FCM VAPID key not configured. Skipping token retrieval.");
                    return null;
                }

                const currentToken = await getToken(messaging, {
                    vapidKey: vapidKey
                });

                if (currentToken) {
                    console.log("FCM Token:", currentToken);
                    // Save token to user profile
                    await set(ref(db, `users/${userId}/fcmTokens/${currentToken}`), true);
                    return currentToken;
                } else {
                    console.warn("No registration token available. Request permission to generate one.");
                }
            } else {
                console.warn("Notification permission denied");
            }
        } catch (error) {
            console.error("An error occurred while retrieving token. ", error);
        }
    },

    // Listen for Foreground Messages (FCM)
    listenForForegroundNotifications: (onNewNotification) => {
        const unsubscribe = onMessage(messaging, (payload) => {
            console.log('Message received. ', payload);
            // Customize notification here
            const notification = {
                title: payload.notification.title,
                message: payload.notification.body,
                type: 'push', // or derive from payload.data
                timestamp: Date.now()
            };
            onNewNotification(notification);
        });

        return unsubscribe;
    }
};
